/**
 * AdminTimelineOptionsPage
 * [UC-021] Timeline options admin — full CRUD in a single table.
 * Add / edit / remove rows inline; percentage-only pricing (no base price).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { api } from '../core/api-client';
import toast from 'react-hot-toast';

interface TimelineOption {
  timelineOptionId?: number;         // omitted for new rows
  name: string;
  description: string;
  percentageIncrease: number;
  _rowKey: string;
}

let __rowSeq = 0;
const newRow = (): TimelineOption => ({
  name: '',
  description: '',
  percentageIncrease: 0,
  _rowKey: `new-${++__rowSeq}`,
});

export function AdminTimelineOptionsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [rows, setRows] = useState<TimelineOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageError, setPageError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      setPageError('');
      const data = await api.get<
        | { items: { timelineOptionId: number; name: string; description: string; percentageIncrease: number }[] }
        | { timelineOptionId: number; name: string; description: string; percentageIncrease: number }[]
      >('/timeline-options');
      const items = Array.isArray(data) ? data : (data.items || []);
      const sorted = [...items].sort((a, b) => a.timelineOptionId - b.timelineOptionId);
      setRows(sorted.map((o) => ({
        timelineOptionId: o.timelineOptionId,
        name: o.name || '',
        description: o.description || '',
        percentageIncrease: Number(o.percentageIncrease) || 0,
        _rowKey: `db-${o.timelineOptionId}`,
      })));
    } catch (e: unknown) {
      console.error(e);
      setPageError(t('adminTimelineOptions.loadError', 'Failed to load timeline options'));
    } finally {
      setLoading(false);
    }
  };

  const update = (rowKey: string, patch: Partial<TimelineOption>) => {
    setRows((prev) => prev.map((r) => (r._rowKey === rowKey ? { ...r, ...patch } : r)));
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  const addRow = () => setRows((prev) => [...prev, newRow()]);

  const removeRow = (rowKey: string) => {
    setRows((prev) => prev.filter((r) => r._rowKey !== rowKey));
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    rows.forEach((r) => {
      if (!r.name.trim()) {
        errs[r._rowKey] = t('adminTimelineOptions.nameRequired', 'Name is required');
      } else if (r.percentageIncrease == null || Number.isNaN(Number(r.percentageIncrease))) {
        errs[r._rowKey] = t('adminTimelineOptions.percentageRequired', 'Percentage increase is required');
      } else if (Number(r.percentageIncrease) < 0 || Number(r.percentageIncrease) > 100) {
        errs[r._rowKey] = t('adminTimelineOptions.percentageRange', 'Percentage must be between 0 and 100');
      }
    });
    setRowErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const save = async () => {
    setSubmitError('');
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        options: rows.map((r) => ({
          ...(r.timelineOptionId ? { timelineOptionId: r.timelineOptionId } : {}),
          name: r.name.trim(),
          description: r.description.trim(),
          percentageIncrease: Number(r.percentageIncrease),
        })),
      };
      const updated = await api.put<
        { timelineOptionId: number; name: string; description: string; percentageIncrease: number }[]
      >('/timeline-options', payload);
      const sorted = [...updated].sort((a, b) => a.timelineOptionId - b.timelineOptionId);
      setRows(sorted.map((o) => ({
        timelineOptionId: o.timelineOptionId,
        name: o.name || '',
        description: o.description || '',
        percentageIncrease: Number(o.percentageIncrease) || 0,
        _rowKey: `db-${o.timelineOptionId}`,
      })));
      toast.success(t('adminTimelineOptions.saved', 'Timeline options saved'));
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { detail?: string; error?: { message?: string } } } };
      const detail = err?.response?.data?.detail || err?.response?.data?.error?.message;
      if (err?.response?.status === 400 && detail) setSubmitError(detail);
      else setSubmitError(t('adminTimelineOptions.updateError', 'Failed to save timeline options'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-[var(--accent-primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (pageError) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600 mb-4">{pageError}</p>
        <button
          onClick={load}
          className="px-4 py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-sm font-medium transition-colors"
        >
          {t('common.retry', 'Retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[var(--text-primary)]">
            {t('adminTimelineOptions.title', 'Timeline Options')}
          </h1>
          <p className="mt-1.5 text-sm text-[var(--text-secondary)]">
            {t('adminTimelineOptions.description', 'Define timeline options users can pick during development initiation. Percentage increase is applied to the project price.')}
          </p>
        </div>
        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-sm font-medium transition-colors shrink-0"
        >
          <Plus className="w-4 h-4" />
          {t('adminTimelineOptions.addOption', 'Add option')}
        </button>
      </div>

      <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-[var(--text-muted)] text-sm mb-4">
              {t('adminTimelineOptions.empty', 'No timeline options yet. Add one to get started.')}
            </p>
            <button
              type="button"
              onClick={addRow}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('adminTimelineOptions.addOption', 'Add option')}
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border-subtle)] bg-[var(--surface-raised)]">
                  <th className="px-4 py-3 text-left font-semibold text-[var(--text-secondary)]">
                    {t('adminTimelineOptions.col.name', 'Name')}
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-[var(--text-secondary)]">
                    {t('adminTimelineOptions.col.description', 'Description')}
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-[var(--text-secondary)] w-40">
                    {t('adminTimelineOptions.col.percentage', 'Increase (%)')}
                  </th>
                  <th className="px-4 py-3 w-12" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <React.Fragment key={row._rowKey}>
                    <tr className="border-b border-[var(--border-subtle)] last:border-0">
                      <td className="px-4 py-3 align-top">
                        <input
                          type="text"
                          value={row.name}
                          onChange={(e) => update(row._rowKey, { name: e.target.value })}
                          placeholder={t('adminTimelineOptions.namePlaceholder', 'e.g. Standard, Express, Rush')}
                          className={`w-full px-3 py-2 bg-[var(--surface-base)] border rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-1 ${
                            rowErrors[row._rowKey]
                              ? 'border-red-500 focus:ring-red-500'
                              : 'border-[var(--border-subtle)] focus:border-[var(--accent-primary)] focus:ring-[var(--accent-primary)]'
                          }`}
                        />
                      </td>
                      <td className="px-4 py-3 align-top">
                        <input
                          type="text"
                          value={row.description}
                          onChange={(e) => update(row._rowKey, { description: e.target.value })}
                          placeholder={t('adminTimelineOptions.descriptionPlaceholder', 'Optional')}
                          className="w-full px-3 py-2 bg-[var(--surface-base)] border border-[var(--border-subtle)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:border-[var(--accent-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)]"
                        />
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-2 justify-end">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={row.percentageIncrease}
                            onChange={(e) =>
                              update(row._rowKey, { percentageIncrease: parseFloat(e.target.value) || 0 })
                            }
                            placeholder="0"
                            className={`w-full px-3 py-2 bg-[var(--surface-base)] border rounded-lg text-[var(--text-primary)] text-right placeholder-[var(--text-muted)] focus:outline-none focus:ring-1 ${
                              rowErrors[row._rowKey]
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-[var(--border-subtle)] focus:border-[var(--accent-primary)] focus:ring-[var(--accent-primary)]'
                            }`}
                          />
                          <span className="text-[var(--text-muted)] text-sm">%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        <button
                          type="button"
                          onClick={() => removeRow(row._rowKey)}
                          className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-[var(--text-muted)] hover:text-red-600 hover:bg-red-500/10 transition-colors"
                          title={t('common.remove', 'Remove')}
                          aria-label={t('common.remove', 'Remove')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                    {rowErrors[row._rowKey] && (
                      <tr className="bg-red-500/5">
                        <td colSpan={4} className="px-4 py-1.5 text-xs text-red-600">
                          {rowErrors[row._rowKey]}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {submitError && (
        <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-600">
          {submitError}
        </div>
      )}

      <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate('/admin/dashboard')}
          className="w-full sm:w-auto px-5 py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-sm font-medium transition-colors"
        >
          {t('common.cancel', 'Cancel')}
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="w-full sm:w-auto px-5 py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? t('adminTimelineOptions.saving', 'Saving…') : t('adminTimelineOptions.saveChanges', 'Save changes')}
        </button>
      </div>
    </div>
  );
}

export default AdminTimelineOptionsPage;
